/**
 * InboxPanel — AI-powered Instagram DM manager for The Snuggle Prints admin.
 *
 * Layout (desktop): [ThreadList | Conversation | RightRail (profile + settings)]
 * Layout (mobile): single column, tap a thread to open, back to return.
 *
 * Backend: /api/inbox/* (see backend/inbox.py)
 *   • Threads, per-thread messages, per-thread ai_paused toggle
 *   • Mock incoming DM button for testing without Meta creds
 *   • Global settings: system prompt, keyword triggers, model, ai_enabled
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Send, Bot, User as UserIcon, RefreshCw, Play, Pause, Trash2,
  MessageSquare, Settings as SettingsIcon, Sparkles, AlertTriangle, Zap,
  Inbox as InboxIcon, ChevronLeft, Instagram, Copy, X, Clock,
} from "lucide-react";

/* ── Follow-up status helper ──────────────────────────────────────────────── */
/**
 * Returns an object describing this thread's follow-up state:
 *   { type: 'scheduled'|'pending'|'sent'|'expired'|'disabled', scheduledAt?, sentAt?, windowClosesAt? }
 * Returns null when there's no customer message to base a follow-up on.
 */
function getFollowupStatus(thread) {
  const { last_customer_message_at, last_followup_sent_at, followup_enabled } = thread || {};
  if (followup_enabled === false) return { type: "disabled" };

  // No customer message timestamp yet (legacy thread — backfill will populate it soon)
  // Still return an actionable state so the admin can send manually
  if (!last_customer_message_at) {
    return { type: "no_time" };
  }

  const lastMsgAt = new Date(last_customer_message_at);
  const now = new Date();

  // Already sent this epoch? (sent_at >= last customer msg)
  if (last_followup_sent_at) {
    const sentAt = new Date(last_followup_sent_at);
    if (sentAt >= lastMsgAt) return { type: "sent", sentAt };
  }

  const scheduledAt    = new Date(lastMsgAt.getTime() + 23 * 60 * 60 * 1000);
  const windowClosesAt = new Date(lastMsgAt.getTime() + 24 * 60 * 60 * 1000);

  if (now >= windowClosesAt) return { type: "expired" };
  // Between 23h-24h: cron should fire soon, but might not have yet
  if (now >= scheduledAt)    return { type: "pending", scheduledAt, windowClosesAt };
  return { type: "scheduled", scheduledAt, windowClosesAt };
}

function fmtTime(date) {
  if (!date) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function fmtDateTime(date) {
  if (!date) return "";
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();
  const timeStr = fmtTime(date);
  if (isToday)    return `today at ${timeStr}`;
  if (isTomorrow) return `tomorrow at ${timeStr}`;
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} at ${timeStr}`;
}

function fmtCountdown(date) {
  const diff = Math.max(0, date - new Date());
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return "< 1m";
}

export default function InboxPanel({ adminPassword, apiBase }) {
  const [tab, setTab] = useState("inbox"); // inbox | settings
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [metaConfigured, setMetaConfigured] = useState(false);
  const [oauthConnected, setOauthConnected] = useState(false);
  const [igUsername, setIgUsername] = useState("");
  const [pageName, setPageName] = useState("");
  const [showMock, setShowMock] = useState(false);
  const [togglingAi, setTogglingAi] = useState(false);

  const headers = useMemo(
    () => ({ "x-admin-password": adminPassword }),
    [adminPassword]
  );

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${apiBase}/api/inbox/threads`, { headers });
      setThreads(r.data.threads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load threads");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const r = await axios.get(`${apiBase}/api/inbox/settings`, { headers });
      setSettings(r.data.settings);
      setMetaConfigured(!!r.data.meta_configured);
      setOauthConnected(!!r.data.oauth_connected);
      setIgUsername(r.data.ig_username || "");
      setPageName(r.data.page_name || "");
    } catch {
      /* handled at first render */
    }
  };

  useEffect(() => {
    fetchThreads();
    fetchSettings();
    // Poll every 15s so new DMs surface without a manual refresh
    const iv = setInterval(fetchThreads, 15000);
    // Detect OAuth callback result (browser lands on /admin?ig_oauth=ok after FB flow)
    const sp = new URLSearchParams(window.location.search);
    const oauthResult = sp.get("ig_oauth");
    if (oauthResult === "ok") {
      const ig = sp.get("ig") || "";
      toast.success(`Instagram connected${ig ? ` as @${ig}` : ""}!`);
      const clean = new URL(window.location.href);
      clean.searchParams.delete("ig_oauth");
      clean.searchParams.delete("ig");
      window.history.replaceState({}, "", clean.toString());
    } else if (oauthResult === "error") {
      const reason = sp.get("reason") || "unknown";
      toast.error(`Instagram connection failed: ${reason.replace(/_/g, " ")}`);
      const clean = new URL(window.location.href);
      clean.searchParams.delete("ig_oauth");
      clean.searchParams.delete("reason");
      window.history.replaceState({}, "", clean.toString());
    }
    return () => clearInterval(iv);
  }, []);

  const unreadCount = threads.reduce((n, t) => n + (t.unread_admin || 0), 0);
  const handoffCount = threads.filter((t) => t.handoff_flagged).length;
  const aiEnabled = settings?.ai_enabled !== false; // default true until loaded

  const toggleAiGlobal = async () => {
    setTogglingAi(true);
    try {
      const next = !aiEnabled;
      const r = await axios.put(
        `${apiBase}/api/inbox/settings`,
        { ai_enabled: next },
        { headers }
      );
      setSettings(r.data.settings);
      toast.success(next ? "AI replies resumed" : "AI replies paused for all threads");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update AI state");
    } finally {
      setTogglingAi(false);
    }
  };

  return (
    <div className="bg-card border border-border/60 p-4 sm:p-6" data-testid="inbox-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <InboxIcon className="w-6 h-6" /> Inbox
            {unreadCount > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full" data-testid="inbox-unread-badge">
                {unreadCount} unread
              </span>
            )}
            {handoffCount > 0 && (
              <span className="bg-amber-500/20 text-amber-500 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {handoffCount} need you
              </span>
            )}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            AI replies to Instagram DMs using your system prompt.{" "}
            {metaConfigured ? (
              <span className="text-emerald-500">Instagram connected.</span>
            ) : (
              <span className="text-amber-500">Mock mode — Meta creds not set.</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleAiGlobal}
            disabled={togglingAi || !settings}
            className={`text-sm px-3 py-2 rounded-md flex items-center gap-1.5 font-medium transition-colors ${
              aiEnabled
                ? "bg-amber-500/15 text-amber-600 hover:bg-amber-500/25 border border-amber-500/30"
                : "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 border border-emerald-500/30"
            } disabled:opacity-50`}
            data-testid="inbox-toggle-ai-global"
          >
            {togglingAi
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : aiEnabled
                ? <Pause className="w-4 h-4" />
                : <Play className="w-4 h-4" />
            }
            {aiEnabled ? "Pause AI" : "Resume AI"}
          </button>
          <button
            onClick={() => setShowMock(true)}
            className="text-sm px-3 py-2 border border-border rounded-md hover:bg-muted flex items-center gap-1.5"
            data-testid="inbox-mock-open"
          >
            <Zap className="w-4 h-4" /> Simulate DM
          </button>
          <button
            onClick={fetchThreads}
            className="text-sm px-3 py-2 border border-border rounded-md hover:bg-muted flex items-center gap-1.5"
            data-testid="inbox-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => setTab(tab === "settings" ? "inbox" : "settings")}
            className={`text-sm px-3 py-2 rounded-md flex items-center gap-1.5 ${
              tab === "settings" ? "bg-primary text-primary-foreground" : "border border-border hover:bg-muted"
            }`}
            data-testid="inbox-tab-settings"
          >
            <SettingsIcon className="w-4 h-4" /> {tab === "settings" ? "Close settings" : "Settings"}
          </button>
        </div>
      </div>

      {/* Global AI paused banner */}
      {!aiEnabled && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400" data-testid="inbox-ai-paused-banner">
          <Pause className="w-4 h-4 shrink-0" />
          <span className="text-sm font-medium flex-1">
            AI replies are paused globally — no thread will receive an automatic response until you resume.
          </span>
          <button
            onClick={toggleAiGlobal}
            disabled={togglingAi}
            className="text-xs px-3 py-1.5 bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 rounded-md hover:bg-emerald-500/25 flex items-center gap-1 disabled:opacity-50"
            data-testid="inbox-ai-resume-banner-btn"
          >
            <Play className="w-3 h-3" /> Resume
          </button>
        </div>
      )}

      {tab === "settings" ? (
        <SettingsView
          settings={settings}
          headers={headers}
          apiBase={apiBase}
          onSaved={(s) => setSettings(s)}
          oauthConnected={oauthConnected}
          igUsername={igUsername}
          pageName={pageName}
          onConnectionChanged={() => { fetchSettings(); fetchThreads(); }}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4">
          <ThreadList
            threads={threads}
            selectedId={selectedId}
            onSelect={setSelectedId}
            loading={loading}
          />
          <div className="min-w-0">
            {selectedId ? (
              <ThreadDetail
                key={selectedId}
                threadId={selectedId}
                headers={headers}
                apiBase={apiBase}
                onChanged={fetchThreads}
                onBack={() => setSelectedId(null)}
              />
            ) : (
              <EmptyDetail />
            )}
          </div>
        </div>
      )}

      {showMock && (
        <MockDialog
          onClose={() => setShowMock(false)}
          headers={headers}
          apiBase={apiBase}
          onSent={(tid) => {
            setShowMock(false);
            setSelectedId(tid);
            fetchThreads();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------ Thread list ------------------------------ */
function ThreadList({ threads, selectedId, onSelect, loading }) {
  if (loading && threads.length === 0) {
    return (
      <div className="border border-border rounded-md p-6 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (threads.length === 0) {
    return (
      <div className="border border-dashed border-border rounded-md p-6 text-center text-sm text-muted-foreground">
        No conversations yet. Use <span className="text-foreground font-medium">Simulate DM</span> to try one out.
      </div>
    );
  }
  return (
    <div className="border border-border rounded-md divide-y divide-border/60 max-h-[70vh] overflow-y-auto" data-testid="inbox-thread-list">
      {threads.map((t) => (
        <button
          key={t.id}
          onClick={() => onSelect(t.id)}
          className={`w-full text-left px-3 py-3 transition-colors ${
            selectedId === t.id ? "bg-primary/10" : "hover:bg-muted/50"
          }`}
          data-testid={`inbox-thread-item-${t.id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {t.platform === "instagram" ? (
                <Instagram className="w-4 h-4 text-pink-500 shrink-0" />
              ) : (
                <Zap className="w-4 h-4 text-amber-500 shrink-0" />
              )}
              <span className="text-sm font-medium truncate">
                {t.customer_name || t.customer_handle || "Customer"}
              </span>
              {t.pet_name && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                  🐾 {t.pet_name}
                </span>
              )}
            </div>
            {t.unread_admin > 0 && (
              <span className="bg-primary text-primary-foreground text-[10px] px-1.5 rounded-full">
                {t.unread_admin}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {t.last_message_preview || "(no messages yet)"}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {t.ai_paused && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex items-center gap-1">
                <Pause className="w-2.5 h-2.5" /> AI paused
              </span>
            )}
            {t.handoff_flagged && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5" /> {t.handoff_reason || "needs human"}
              </span>
            )}
            {(() => {
              const fs = getFollowupStatus(t);
              if (!fs) return null;
              if (fs.type === "no_time") return (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary flex items-center gap-1" title="Follow-up enabled — tap to send manually">
                  <Sparkles className="w-2.5 h-2.5" /> follow-up on
                </span>
              );
              if (fs.type === "scheduled") return (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary flex items-center gap-1" title={`Follow-up scheduled for ${fmtDateTime(fs.scheduledAt)}`}>
                  <Clock className="w-2.5 h-2.5" /> {fmtCountdown(fs.scheduledAt)}
                </span>
              );
              if (fs.type === "pending") return (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 flex items-center gap-1" title="Follow-up overdue — cron should fire soon or trigger manually">
                  <Clock className="w-2.5 h-2.5" /> follow-up overdue
                </span>
              );
              if (fs.type === "sent") return (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 flex items-center gap-1" title={`Follow-up sent at ${fmtTime(fs.sentAt)}`}>
                  <Sparkles className="w-2.5 h-2.5" /> sent {fmtTime(fs.sentAt)}
                </span>
              );
              if (fs.type === "expired") return (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" /> window closed
                </span>
              );
              return null;
            })()}
          </div>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------ Thread detail ------------------------------ */
function ThreadDetail({ threadId, headers, apiBase, onChanged, onBack }) {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingFollowup, setSendingFollowup] = useState(false);
  const scrollRef = useRef(null);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${apiBase}/api/inbox/threads/${threadId}`, { headers });
      setThread(r.data.thread);
      setMessages(r.data.messages || []);
      // Scroll to bottom
      setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load thread");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    const iv = setInterval(fetchDetail, 10000);
    return () => clearInterval(iv);
  }, [threadId]);

  const sendReply = async () => {
    const t = reply.trim();
    if (!t) return;
    setSending(true);
    try {
      await axios.post(
        `${apiBase}/api/inbox/threads/${threadId}/reply`,
        { text: t },
        { headers }
      );
      setReply("");
      await fetchDetail();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally {
      setSending(false);
    }
  };

  const toggleAi = async () => {
    if (!thread) return;
    try {
      await axios.post(
        `${apiBase}/api/inbox/threads/${threadId}/toggle-ai`,
        { paused: !thread.ai_paused },
        { headers }
      );
      await fetchDetail();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const toggleFollowup = async () => {
    if (!thread) return;
    const currentEnabled = thread.followup_enabled !== false; // default true
    try {
      await axios.post(
        `${apiBase}/api/inbox/threads/${threadId}/toggle-followup`,
        { paused: currentEnabled }, // paused=true → followup_enabled=false
        { headers }
      );
      await fetchDetail();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const sendFollowupNow = async () => {
    setSendingFollowup(true);
    try {
      await axios.post(
        `${apiBase}/api/inbox/threads/${threadId}/send-followup`,
        {},
        { headers }
      );
      toast.success("Follow-up sent!");
      await fetchDetail();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send follow-up");
    } finally {
      setSendingFollowup(false);
    }
  };

  const deleteThread = async () => {
    try {
      await axios.delete(`${apiBase}/api/inbox/threads/${threadId}`, { headers });
      toast.success("Thread deleted");
      onBack?.();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading && !thread) {
    return (
      <div className="border border-border rounded-md p-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!thread) return <EmptyDetail />;

  return (
    <div className="border border-border rounded-md flex flex-col h-[70vh]" data-testid="inbox-thread-detail">
      {/* Header */}
      <div className="border-b border-border px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={onBack} className="lg:hidden p-1 hover:bg-muted rounded" data-testid="inbox-thread-back">
            <ChevronLeft className="w-4 h-4" />
          </button>
          {thread.platform === "instagram" ? (
            <Instagram className="w-5 h-5 text-pink-500" />
          ) : (
            <Zap className="w-5 h-5 text-amber-500" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">
              {thread.customer_name || thread.customer_handle}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {thread.pet_name && <>🐾 {thread.pet_name} · </>}
              {thread.customer_email || thread.customer_handle}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={toggleAi}
            className={`text-xs px-2.5 py-1.5 rounded-md flex items-center gap-1.5 ${
              thread.ai_paused
                ? "bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/25"
                : "bg-muted text-foreground hover:bg-muted/70"
            }`}
            data-testid="inbox-thread-toggle-ai"
          >
            {thread.ai_paused ? (
              <><Play className="w-3.5 h-3.5" /> Resume AI</>
            ) : (
              <><Pause className="w-3.5 h-3.5" /> Pause AI</>
            )}
          </button>
          <button
            onClick={toggleFollowup}
            title={thread.followup_enabled !== false ? "Disable 23h follow-up for this thread" : "Enable 23h follow-up for this thread"}
            className={`text-xs px-2.5 py-1.5 rounded-md flex items-center gap-1.5 ${
              thread.followup_enabled !== false
                ? "bg-muted text-foreground hover:bg-muted/70"
                : "bg-amber-500/15 text-amber-600 hover:bg-amber-500/25 border border-amber-500/30"
            }`}
            data-testid="inbox-thread-toggle-followup"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {thread.followup_enabled !== false ? "Follow-up on" : "Follow-up off"}
          </button>
          <button
            onClick={deleteThread}
            className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded"
            title="Delete thread"
            data-testid="inbox-thread-delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Follow-up status bar */}
      {(() => {
        const fs = getFollowupStatus(thread);
        if (!fs || fs.type === "disabled") return null;

        const sendBtn = (
          <button
            onClick={sendFollowupNow}
            disabled={sendingFollowup}
            className="ml-auto shrink-0 text-xs px-2.5 py-1 rounded-md bg-primary/15 text-primary hover:bg-primary/25 flex items-center gap-1 disabled:opacity-50 font-medium"
            data-testid="inbox-send-followup-now"
          >
            {sendingFollowup ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Send now
          </button>
        );

        if (fs.type === "no_time") return (
          <div className="border-b border-primary/20 bg-primary/5 px-4 py-2 text-xs text-primary flex items-center gap-2" data-testid="inbox-followup-status">
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span>Follow-up enabled — trigger manually to re-engage this customer</span>
            {sendBtn}
          </div>
        );
        if (fs.type === "scheduled") return (
          <div className="border-b border-primary/20 bg-primary/5 px-4 py-2 text-xs text-primary flex items-center gap-2" data-testid="inbox-followup-status">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span>Follow-up scheduled for <strong>{fmtDateTime(fs.scheduledAt)}</strong> <span className="text-primary/60">({fmtCountdown(fs.scheduledAt)} from now)</span></span>
            {sendBtn}
          </div>
        );
        if (fs.type === "pending") return (
          <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2" data-testid="inbox-followup-status">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span>Follow-up overdue — cron will send before <strong>{fmtTime(fs.windowClosesAt)}</strong>. Send it now if urgent.</span>
            {sendBtn}
          </div>
        );
        if (fs.type === "sent") return (
          <div className="border-b border-emerald-500/20 bg-emerald-500/5 px-4 py-2 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-2" data-testid="inbox-followup-status">
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span>Follow-up sent at <strong>{fmtTime(fs.sentAt)}</strong></span>
            <button
              onClick={sendFollowupNow}
              disabled={sendingFollowup}
              className="ml-auto shrink-0 text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground hover:text-foreground disabled:opacity-50"
              data-testid="inbox-send-followup-now"
            >
              {sendingFollowup ? <Loader2 className="w-3 h-3 animate-spin" /> : "Send again"}
            </button>
          </div>
        );
        if (fs.type === "expired") return (
          <div className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground flex items-center gap-2" data-testid="inbox-followup-status">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span>Meta 24h window closed — DMs may be rejected by the API.</span>
            {sendBtn}
          </div>
        );
        return null;
      })()}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="inbox-messages">
        {messages.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-10">No messages yet</div>
        ) : (
          messages.map((m) => <MessageBubble key={m.id} msg={m} />)
        )}
      </div>

      {/* Handoff banner */}
      {thread.handoff_flagged && (
        <div className="border-t border-amber-500/30 bg-amber-500/10 text-amber-600 px-4 py-2 text-xs flex items-center gap-2" data-testid="inbox-handoff-banner">
          <AlertTriangle className="w-4 h-4" />
          Handoff flagged{thread.handoff_reason && <>: <strong>{thread.handoff_reason}</strong></>}. AI is paused until you resume.
        </div>
      )}

      {/* Compose */}
      <div className="border-t border-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                sendReply();
              }
            }}
            placeholder="Reply as human… (Cmd/Ctrl+Enter to send). Sending pauses the AI on this thread."
            rows={2}
            className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="inbox-reply-input"
          />
          <button
            onClick={sendReply}
            disabled={sending || !reply.trim()}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            data-testid="inbox-reply-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isCustomer = msg.direction === "in";
  const isSystem = msg.author === "system";
  if (isSystem) {
    return (
      <div className="text-center text-[11px] text-muted-foreground italic" data-testid={`inbox-msg-${msg.id}`}>
        {msg.text}
      </div>
    );
  }
  const authorLabel = { ai: "AI", admin: "You", customer: "Customer" }[msg.author] || msg.author;
  const iconMap = {
    ai: <Bot className="w-3 h-3" />,
    admin: <UserIcon className="w-3 h-3" />,
    customer: <UserIcon className="w-3 h-3" />,
  };
  return (
    <div className={`flex ${isCustomer ? "justify-start" : "justify-end"}`} data-testid={`inbox-msg-${msg.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
        isCustomer
          ? "bg-muted text-foreground rounded-bl-sm"
          : msg.author === "ai"
            ? "bg-primary/15 text-foreground rounded-br-sm"
            : "bg-primary text-primary-foreground rounded-br-sm"
      }`}>
        <div className={`text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1 ${
          isCustomer ? "text-muted-foreground" : "opacity-70"
        }`}>
          {iconMap[msg.author]} {authorLabel}
        </div>
        <div className="whitespace-pre-wrap break-words">{msg.text}</div>
      </div>
    </div>
  );
}

function EmptyDetail() {
  return (
    <div className="border border-dashed border-border rounded-md p-10 text-center text-sm text-muted-foreground">
      <MessageSquare className="w-8 h-8 mx-auto mb-3 opacity-50" />
      Select a conversation to view messages.
    </div>
  );
}

/* ------------------------------ Settings view ------------------------------ */
function SettingsView({ settings, headers, apiBase, onSaved, oauthConnected, igUsername, pageName, onConnectionChanged }) {
  const [draft, setDraft] = useState(settings || null);
  const [saving, setSaving] = useState(false);
  const [newKw, setNewKw] = useState("");
  // Separate prompt draft so we can track dirty state without triggering global saves
  const [promptText, setPromptText] = useState(settings?.system_prompt || "");
  const [promptDirty, setPromptDirty] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);

  useEffect(() => {
    setDraft(settings);
    setPromptText(settings?.system_prompt || "");
    setPromptDirty(false);
  }, [settings]);

  if (!draft) {
    return (
      <div className="border border-border rounded-md p-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${apiBase}/api/inbox/settings`, patch, { headers });
      onSaved?.(r.data.settings);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      const r = await axios.put(`${apiBase}/api/inbox/settings`, { system_prompt: promptText }, { headers });
      onSaved?.(r.data.settings);
      setPromptDirty(false);
      toast.success("Prompt saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPrompt(false);
    }
  };

  const addKeyword = () => {
    const k = newKw.trim();
    if (!k) return;
    const next = [...(draft.keyword_triggers || []), k];
    setDraft({ ...draft, keyword_triggers: next });
    save({ keyword_triggers: next });
    setNewKw("");
  };

  const removeKeyword = (k) => {
    const next = (draft.keyword_triggers || []).filter((x) => x !== k);
    setDraft({ ...draft, keyword_triggers: next });
    save({ keyword_triggers: next });
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="inbox-settings-view">
      {/* Instagram OAuth connection card */}
      <ConnectionCard
        apiBase={apiBase}
        headers={headers}
        oauthConnected={oauthConnected}
        igUsername={igUsername}
        pageName={pageName}
        onConnectionChanged={onConnectionChanged}
      />

      {/* Master toggle */}
      <div className="border border-border rounded-md p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> AI auto-reply
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Master switch. Turn off to make every incoming DM wait for a human.
            </p>
          </div>
          <button
            onClick={() => {
              const next = !draft.ai_enabled;
              setDraft({ ...draft, ai_enabled: next });
              save({ ai_enabled: next });
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              draft.ai_enabled ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"
            }`}
            data-testid="inbox-setting-ai-enabled"
          >
            {draft.ai_enabled ? "On" : "Off"}
          </button>
        </div>
      </div>

      {/* Follow-up automation */}
      <div className="border border-border rounded-md p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> 23-hour follow-up
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Automatically sends an AI follow-up to customers who haven't heard back,
              right before Meta's 24-hour messaging window closes. Runs every 15 minutes.
            </p>
          </div>
          <button
            onClick={() => {
              const next = !draft.followup_enabled;
              setDraft({ ...draft, followup_enabled: next });
              save({ followup_enabled: next });
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium shrink-0 ${
              draft.followup_enabled !== false ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"
            }`}
            data-testid="inbox-setting-followup-enabled"
          >
            {draft.followup_enabled !== false ? "On" : "Off"}
          </button>
        </div>

        {/* Follow-up prompt template */}
        {draft.followup_enabled !== false && (
          <div className="pt-3 border-t border-border/60">
            <label className="text-xs font-medium text-foreground block mb-1">Follow-up instruction for AI</label>
            <p className="text-xs text-muted-foreground mb-2">
              This instruction is sent to Claude alongside the conversation history to generate the follow-up.
              Edit to change the tone or goal.
            </p>
            <textarea
              rows={4}
              value={draft.followup_message_template ?? ""}
              onChange={(e) => setDraft({ ...draft, followup_message_template: e.target.value })}
              onBlur={() => {
                if (draft.followup_message_template !== settings.followup_message_template) {
                  save({ followup_message_template: draft.followup_message_template });
                }
              }}
              placeholder="e.g. Review the conversation and send a warm check-in message…"
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary resize-y"
              data-testid="inbox-setting-followup-template"
            />
          </div>
        )}
      </div>

      {/* Message delivery settings */}
      <div className="border border-border rounded-md p-4 space-y-4">
        <div className="text-sm font-semibold flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" /> Message delivery
        </div>

        {/* Split toggle */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Split reply into multiple messages</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Each line break in the AI reply is sent as a separate DM, simulating natural conversation.
            </p>
          </div>
          <button
            onClick={() => {
              const next = !draft.split_messages;
              setDraft({ ...draft, split_messages: next });
              save({ split_messages: next });
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium shrink-0 ${
              draft.split_messages ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"
            }`}
            data-testid="inbox-setting-split-messages"
          >
            {draft.split_messages ? "On" : "Off"}
          </button>
        </div>

        {/* Typing speed — only shown when split is on */}
        {draft.split_messages && (
          <div className="pt-3 border-t border-border/60">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium">Typing speed</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Characters per second. Lower = slower (more human). Delay is capped at 8 s per message.
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <input
                    type="range"
                    min="3"
                    max="25"
                    step="1"
                    value={draft.typing_speed_cps ?? 10}
                    onChange={(e) => setDraft({ ...draft, typing_speed_cps: Number(e.target.value) })}
                    onMouseUp={() => save({ typing_speed_cps: draft.typing_speed_cps })}
                    onTouchEnd={() => save({ typing_speed_cps: draft.typing_speed_cps })}
                    className="flex-1 accent-primary"
                    data-testid="inbox-setting-typing-speed"
                  />
                  <span className="text-sm font-mono w-16 text-right tabular-nums text-muted-foreground">
                    {draft.typing_speed_cps ?? 10} ch/s
                  </span>
                </div>
                {/* Live preview of delays */}
                <p className="text-xs text-muted-foreground mt-2">
                  Preview: 30-char message →{" "}
                  <strong>{Math.max(1.5, Math.min(30 / (draft.typing_speed_cps ?? 10), 8)).toFixed(1)}s</strong>
                  {" · "}80-char message →{" "}
                  <strong>{Math.max(1.5, Math.min(80 / (draft.typing_speed_cps ?? 10), 8)).toFixed(1)}s</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Debounce window */}
        <div className="pt-3 border-t border-border/60">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Message grouping window</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Wait this long after the customer's last message before replying. Prevents sending
                separate replies to each line of a multi-message thought.
              </p>
            </div>
            <div className="flex gap-1 shrink-0" data-testid="inbox-setting-debounce">
              {[0, 1, 2, 3, 5].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setDraft({ ...draft, debounce_seconds: s });
                    save({ debounce_seconds: s });
                  }}
                  className={`px-2.5 py-1.5 rounded text-xs font-medium transition ${
                    (draft.debounce_seconds ?? 2) === s
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  data-testid={`inbox-setting-debounce-${s}`}
                >
                  {s === 0 ? "Off" : `${s}s`}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* System prompt — full editor */}
      <div className="border border-border rounded-md p-4" data-testid="inbox-system-prompt-editor">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <label className="text-sm font-semibold flex items-center gap-2">
              <Bot className="w-4 h-4 text-primary" /> System prompt
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Everything Claude knows about your business. Add your FAQs, full price list, and
              rules here. The AI will only answer from what is written — add the line below to
              stop it guessing outside this prompt.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground tabular-nums">
              {promptText.length.toLocaleString()} chars
            </span>
            <button
              onClick={savePrompt}
              disabled={!promptDirty || savingPrompt}
              className={`text-sm px-4 py-2 rounded-md flex items-center gap-1.5 font-medium transition-colors ${
                promptDirty
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground cursor-default"
              } disabled:opacity-60`}
              data-testid="inbox-prompt-save-btn"
            >
              {savingPrompt ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {promptDirty ? "Save prompt" : "Saved"}
            </button>
          </div>
        </div>

        {/* Strict-mode tip */}
        <div className="mb-3 px-3 py-2.5 rounded-md bg-primary/5 border border-primary/20 text-xs text-muted-foreground">
          <strong className="text-foreground">Tip — strict mode:</strong> add this line anywhere in your prompt to stop the AI answering questions that aren't covered:
          <br />
          <code className="mt-1 block text-xs bg-muted px-2 py-1 rounded select-all">
            If a customer asks something not covered in this prompt, reply: "Great question! Let me check that for you — our team will follow up shortly."
          </code>
        </div>

        <textarea
          rows={28}
          value={promptText}
          onChange={(e) => {
            setPromptText(e.target.value);
            setPromptDirty(true);
          }}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "s") {
              e.preventDefault();
              if (promptDirty) savePrompt();
            }
          }}
          placeholder="Write your full system prompt here. Include: tone, pricing, FAQs, what to escalate, and a strict-mode fallback line."
          className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary resize-y leading-relaxed"
          data-testid="inbox-setting-system-prompt"
          spellCheck={false}
        />
        <p className="text-xs text-muted-foreground mt-1.5">
          Ctrl+S / Cmd+S to save quickly.
        </p>
      </div>

      {/* Brand voice */}
      <div className="border border-border rounded-md p-4">
        <label className="text-sm font-semibold">Brand voice</label>
        <p className="text-xs text-muted-foreground mt-1 mb-3">
          One line describing how the AI should sound (Claude sees this alongside the system prompt).
        </p>
        <input
          type="text"
          value={draft.brand_voice || ""}
          onChange={(e) => setDraft({ ...draft, brand_voice: e.target.value })}
          onBlur={() => draft.brand_voice !== settings.brand_voice && save({ brand_voice: draft.brand_voice })}
          className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="inbox-setting-brand-voice"
        />
      </div>

      {/* Keyword triggers */}
      <div className="border border-border rounded-md p-4">
        <label className="text-sm font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" /> Handoff keywords
        </label>
        <p className="text-xs text-muted-foreground mt-1 mb-3">
          If a customer&rsquo;s message contains any of these (case-insensitive), the AI stops and the
          thread is flagged for you.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {(draft.keyword_triggers || []).map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 bg-muted text-foreground text-xs px-2 py-1 rounded-full"
              data-testid={`inbox-keyword-${k.replace(/\s+/g, "-")}`}
            >
              {k}
              <button onClick={() => removeKeyword(k)} className="hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={newKw}
            onChange={(e) => setNewKw(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addKeyword()}
            placeholder="e.g. refund"
            className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="inbox-keyword-input"
          />
          <button
            onClick={addKeyword}
            disabled={!newKw.trim()}
            className="text-sm px-3 py-2 border border-border rounded-md hover:bg-muted disabled:opacity-50"
            data-testid="inbox-keyword-add"
          >
            Add
          </button>
        </div>
      </div>

      {/* Model info */}
      <div className="border border-border rounded-md p-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold">Model</div>
          <div className="text-xs text-muted-foreground mt-1">
            Claude Sonnet 4.5 · Emergent LLM key. Change the id below if you want a different Claude
            variant.
          </div>
        </div>
        <input
          type="text"
          value={draft.model || ""}
          onChange={(e) => setDraft({ ...draft, model: e.target.value })}
          onBlur={() => draft.model !== settings.model && save({ model: draft.model })}
          className="w-72 bg-background border border-border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="inbox-setting-model"
        />
      </div>

      {saving && <div className="text-xs text-muted-foreground">Saving…</div>}
    </div>
  );
}

/* ------------------------------ Instagram connection card ------------------------------ */
function ConnectionCard({ apiBase, headers, oauthConnected, igUsername, pageName, onConnectionChanged }) {
  const [starting, setStarting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [statusData, setStatusData] = useState(null);

  useEffect(() => {
    axios.get(`${apiBase}/api/inbox/oauth/status`, { headers })
      .then((r) => setStatusData(r.data))
      .catch(() => {});
  }, [oauthConnected]);

  const connect = async () => {
    setStarting(true);
    try {
      const r = await axios.get(`${apiBase}/api/inbox/oauth/start`, { headers });
      window.location.href = r.data.oauth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start OAuth flow");
      setStarting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Instagram/Facebook connection? The app will fall back to env-var credentials if set.")) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${apiBase}/api/inbox/oauth/disconnect`, { headers });
      toast.success("Disconnected");
      onConnectionChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setDisconnecting(false);
    }
  };

  const subscribeWebhook = async () => {
    setSubscribing(true);
    try {
      const r = await axios.post(`${apiBase}/api/inbox/oauth/subscribe-webhook`, {}, { headers });
      toast.success(`Webhook subscribed: ${r.data.subscribed_fields?.join(", ")}`);
      // Refresh status
      const s = await axios.get(`${apiBase}/api/inbox/oauth/status`, { headers });
      setStatusData(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
    } finally {
      setSubscribing(false);
    }
  };

  const callbackUrl = statusData?.callback_url || "";
  const webhookSubscribed = statusData?.webhook_subscribed;
  const webhookFields = statusData?.webhook_fields || [];

  return (
    <div
      className={`border rounded-md p-4 ${oauthConnected ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}
      data-testid="inbox-connection-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${oauthConnected ? "bg-emerald-500/15" : "bg-muted"}`}>
            <Instagram className={`w-4 h-4 ${oauthConnected ? "text-emerald-500" : "text-muted-foreground"}`} />
          </div>
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              Instagram Account
              {oauthConnected && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 font-medium">
                  Connected
                </span>
              )}
            </div>
            {oauthConnected ? (
              <div className="text-xs text-muted-foreground mt-0.5">
                {igUsername && <span className="font-medium text-foreground">@{igUsername}</span>}
                {pageName && <span> · {pageName}</span>}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5">
                Connect your Facebook Page to enable live Instagram DM replies.
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {oauthConnected ? (
            <button
              onClick={disconnect}
              disabled={disconnecting}
              className="text-xs px-3 py-1.5 border border-destructive/40 text-destructive rounded-md hover:bg-destructive/5 flex items-center gap-1 disabled:opacity-50"
              data-testid="inbox-oauth-disconnect"
            >
              {disconnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
              Disconnect
            </button>
          ) : (
            <button
              onClick={connect}
              disabled={starting}
              className="text-sm px-4 py-2 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-md flex items-center gap-2 disabled:opacity-60 font-medium"
              data-testid="inbox-oauth-connect"
            >
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Instagram className="w-4 h-4" />}
              Connect with Facebook
            </button>
          )}
        </div>
      </div>

      {/* Callback URL info for Meta Dashboard */}
      {callbackUrl && (
        <div className="mt-3 pt-3 border-t border-border/60">
          <p className="text-xs text-muted-foreground mb-1">
            Register this as a <strong>Valid OAuth Redirect URI</strong> in Meta Dashboard → Facebook Login → Settings:
          </p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate" data-testid="inbox-oauth-callback-url">
              {callbackUrl}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(callbackUrl); toast.success("Copied"); }}
              className="p-1.5 hover:bg-muted rounded shrink-0"
              title="Copy"
            >
              <Copy className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </div>
        </div>
      )}

      {/* Webhook subscription status row */}
      {oauthConnected && (
        <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            <span className={`w-2 h-2 rounded-full shrink-0 ${webhookSubscribed ? "bg-emerald-500" : "bg-amber-500"}`} />
            <span className="text-muted-foreground">
              Webhook:&nbsp;
              {webhookSubscribed
                ? <span className="text-emerald-600 font-medium">{webhookFields.join(", ")}</span>
                : <span className="text-amber-600 font-medium">not yet subscribed</span>
              }
            </span>
          </div>
          <button
            onClick={subscribeWebhook}
            disabled={subscribing}
            className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-muted flex items-center gap-1.5 disabled:opacity-50"
            data-testid="inbox-oauth-subscribe-webhook"
          >
            {subscribing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {webhookSubscribed ? "Re-subscribe" : "Subscribe webhook"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Mock dialog ------------------------------ */
function MockDialog({ onClose, headers, apiBase, onSent }) {
  const [handle, setHandle] = useState("@testcustomer");
  const [name, setName] = useState("");
  const [text, setText] = useState("Hey! Can you make a book from a photo of my cat Luna?");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(
        `${apiBase}/api/inbox/mock/incoming`,
        { text, customer_handle: handle, customer_name: name },
        { headers }
      );
      toast.success("DM simulated — AI is replying");
      onSent?.(r.data.thread_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to simulate");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full p-5" data-testid="inbox-mock-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Simulate a DM
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded" data-testid="inbox-mock-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase text-muted-foreground mb-1 block">Customer handle</label>
            <input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
              data-testid="inbox-mock-handle"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-muted-foreground mb-1 block">Customer name (optional)</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
              data-testid="inbox-mock-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-muted-foreground mb-1 block">Message from customer</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
              data-testid="inbox-mock-text"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="text-sm px-3 py-2 border border-border rounded-md hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={sending || !text.trim() || !handle.trim()}
              className="text-sm px-4 py-2 bg-primary text-primary-foreground rounded-md disabled:opacity-50 flex items-center gap-1.5"
              data-testid="inbox-mock-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send DM
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
